(() => {
  const status = document.querySelector('#site-status');
  const say = message => { if (status) status.textContent = message; };
  let editionNavigationId = 0;
  const decodedImages = new Map();

  async function decodeImage(image) {
    if (typeof image.decode === 'function') {
      try {
        await image.decode();
        return image;
      } catch (_) {}
    }
    if (!image.complete) {
      await new Promise(resolve => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });
    }
    return image;
  }

  function preloadImage(src, baseUrl = location.href) {
    if (!src) return Promise.resolve(null);
    const url = new URL(src, baseUrl).href;
    if (decodedImages.has(url)) return decodedImages.get(url);
    const image = new Image();
    image.decoding = 'async';
    image.src = url;
    const promise = decodeImage(image);
    decodedImages.set(url, promise);
    return promise;
  }

  function retainImage(image, baseUrl = location.href) {
    if (!image) return Promise.resolve(null);
    const src = image.currentSrc || image.getAttribute('src');
    if (!src) return Promise.resolve(null);
    const url = new URL(src, baseUrl).href;
    if (decodedImages.has(url)) return decodedImages.get(url);
    const promise = decodeImage(image);
    decodedImages.set(url, promise);
    return promise;
  }

  function applyCoverMarkup(image, template) {
    if (!image || !template) return;
    [...image.attributes].forEach(attribute => {
      if (attribute.name !== 'src') image.removeAttribute(attribute.name);
    });
    [...template.attributes].forEach(attribute => {
      if (attribute.name !== 'src') image.setAttribute(attribute.name, attribute.value);
    });
  }

  const reference = document.querySelector('#ContactReference');
  if (reference && !reference.value) {
    const edition = new URLSearchParams(location.search).get('edition');
    if (edition && /^[A-Z0-9-]{1,30}$/.test(edition)) reference.value = edition;
  }
  document.querySelector('.form-feedback')?.focus();

  function catalogueSearchQuery(value) {
    const query = value.trim();
    if (!query || /^CS-\d{4}(?:-[A-Z0-9]+)*$/i.test(query)) return query.toUpperCase();
    const terms = query.match(/[\p{L}\p{N}]+(?:[.'’-][\p{L}\p{N}]+)*/gu) || [];
    return terms.map(term => `title:${term}`).join(' ');
  }

  document.querySelectorAll('[data-catalogue-search]').forEach(form => {
    form.addEventListener('submit', event => {
      const input = form.querySelector('input[name="q"]');
      if (!input) return;
      const scopedQuery = catalogueSearchQuery(input.value);
      if (!scopedQuery) {
        event.preventDefault();
        input.focus();
        return;
      }
      input.value = scopedQuery;
    });
  });

  document.querySelector('[data-menu]')?.addEventListener('click', event => {
    const button = event.currentTarget;
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    document.querySelector('#main-nav').classList.toggle('is-open', open);
  });

  function syncEditionHead(nextDocument) {
    document.title = nextDocument.title;
    const selectors = [
      'meta[name="description"]',
      'link[rel="canonical"]',
      'meta[name="robots"]',
      'meta[property^="og:"]',
      'meta[name^="twitter:"]',
      'meta[property^="product:"]',
      'script[type="application/ld+json"]'
    ];
    selectors.forEach(selector => {
      document.head.querySelectorAll(selector).forEach(node => node.remove());
      nextDocument.head.querySelectorAll(selector).forEach(node => document.head.append(document.importNode(node, true)));
    });
  }

  async function replaceEditionPage(href, { pushHistory = true, pickerKind = '' } = {}) {
    const currentMain = document.querySelector('main#main');
    const pickerSelector = pickerKind ? `[data-edition-picker="${pickerKind}"]` : '[data-edition-picker]';
    const currentPicker = currentMain?.querySelector(pickerSelector) || currentMain?.querySelector('[data-edition-picker]');
    if (!currentMain || !currentPicker) throw new Error('Edition selector is unavailable.');

    const navigationId = ++editionNavigationId;
    const pickerViewportTop = currentPicker.getBoundingClientRect().top;
    say('Loading the selected edition.');

    const response = await fetch(href, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html', 'X-Requested-With': 'ConcertScoresEdition' }
    });
    if (!response.ok) throw new Error(`Edition request failed with ${response.status}.`);
    const nextDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
    const sourceMain = nextDocument.querySelector('main#main');
    const sourcePicker = sourceMain?.querySelector(pickerSelector) || sourceMain?.querySelector('[data-edition-picker]');
    if (!sourceMain || !sourcePicker) throw new Error('The selected edition page is incomplete.');
    const sourceCover = sourceMain.querySelector('.product-art .score-cover');
    const preparedCover = await preloadImage(sourceCover?.getAttribute('src'), response.url || href);
    if (navigationId !== editionNavigationId) return;

    document.querySelectorAll('audio').forEach(audio => audio.pause());
    const nextMain = document.importNode(sourceMain, true);
    const nextCover = nextMain.querySelector('.product-art .score-cover');
    if (preparedCover && nextCover) {
      applyCoverMarkup(preparedCover, nextCover);
      nextCover.replaceWith(preparedCover);
    }
    const previousScrollBehavior = document.documentElement.style.scrollBehavior;
    const previousOverflowAnchor = document.documentElement.style.overflowAnchor;
    document.documentElement.style.scrollBehavior = 'auto';
    document.documentElement.style.overflowAnchor = 'none';

    currentMain.replaceWith(nextMain);
    syncEditionHead(nextDocument);

    const currentReviewBar = document.querySelector('.review-bar');
    const sourceReviewBar = nextDocument.querySelector('.review-bar');
    if (currentReviewBar && sourceReviewBar) currentReviewBar.replaceWith(document.importNode(sourceReviewBar, true));

    const destination = new URL(response.url || href, location.href);
    if (pushHistory) history.pushState({ concertScoresEdition: true }, '', destination.href);

    const nextPicker = nextMain.querySelector(pickerSelector) || nextMain.querySelector('[data-edition-picker]');
    const correctedScrollTop = Math.max(0, window.scrollY + nextPicker.getBoundingClientRect().top - pickerViewportTop);
    window.scrollTo(0, correctedScrollTop);
    document.documentElement.style.scrollBehavior = previousScrollBehavior;
    requestAnimationFrame(() => { document.documentElement.style.overflowAnchor = previousOverflowAnchor; });

    initDynamicContent(nextMain);
    window.dispatchEvent(new CustomEvent('concert-scores:content-replaced', { detail: { root: nextMain } }));
    const selectedEdition = nextPicker.querySelector('.edition-current strong')?.textContent?.trim();
    nextPicker.querySelector('.edition-current')?.focus({ preventScroll: true });
    if (selectedEdition) say(`${selectedEdition} selected.`);
  }

  function initEditionPicker(root) {
    const editionPickers = [...root.querySelectorAll('[data-edition-picker]')];
    if (!editionPickers.length) return;
    retainImage(root.querySelector('.product-art .score-cover'));
    history.replaceState({ ...(history.state || {}), concertScoresEdition: true }, '', location.href);
    editionPickers.forEach(editionPicker => {
      editionPicker.querySelectorAll('a.edition-option').forEach(link => link.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const destination = new URL(link.href, location.href);
        if (destination.origin !== location.origin) return;
        event.preventDefault();
        replaceEditionPage(destination.href, { pickerKind: editionPicker.dataset.editionPicker }).catch(() => location.assign(destination.href));
      }));
      editionPicker.querySelectorAll('a.edition-option[data-edition-cover-src]').forEach(link => {
        preloadImage(link.dataset.editionCoverSrc);
      });
    });
  }

  function initDynamicContent(root) {
    root.querySelectorAll('[data-dialog]').forEach(button => button.addEventListener('click', () => {
      document.getElementById(button.dataset.dialog)?.showModal();
    }));
    root.querySelectorAll('dialog').forEach(dialog => {
      dialog.querySelector('[data-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        dialog.classList.remove('is-zoomed');
        const zoom = dialog.querySelector('[data-dialog-zoom]');
        if (zoom) {
          zoom.setAttribute('aria-pressed', 'false');
          zoom.setAttribute('aria-label', 'Zoom in');
          zoom.querySelector('[data-zoom-label]').textContent = 'Zoom in';
        }
      });
    });

    const pageDialog = root.querySelector('#page-dialog');
    const openPage = (src, title) => {
      if (!pageDialog) return;
      const img = pageDialog.querySelector('[data-dialog-image]');
      img.src = src;
      img.alt = title;
      img.hidden = false;
      pageDialog.querySelector('#page-dialog-title').textContent = title;
      pageDialog.showModal();
      pageDialog.querySelector('.dialog-page-scroll').scrollTo(0, 0);
    };

    root.querySelector('[data-cover-view]')?.addEventListener('click', event => {
      const img = event.currentTarget.querySelector('img');
      openPage(event.currentTarget.dataset.coverZoomSrc || img.src, img.alt);
    });
    pageDialog?.querySelector('[data-dialog-zoom]')?.addEventListener('click', event => {
      const zoomed = pageDialog.classList.toggle('is-zoomed');
      const button = event.currentTarget;
      button.setAttribute('aria-pressed', String(zoomed));
      button.setAttribute('aria-label', zoomed ? 'Fit to width' : 'Zoom in');
      button.querySelector('[data-zoom-label]').textContent = zoomed ? 'Fit to width' : 'Zoom in';
    });

    const viewer = root.querySelector('[data-viewer]');
    if (viewer) {
      const samples = [...viewer.querySelectorAll('[data-sample-src]')];
      let selected = samples.findIndex(button => button.getAttribute('aria-pressed') === 'true');
      const select = index => {
        selected = (index + samples.length) % samples.length;
        const sample = samples[selected];
        const img = viewer.querySelector('[data-preview-image]');
        img.src = sample.dataset.sampleSrc;
        img.alt = sample.dataset.sampleLabel;
        viewer.querySelector('[data-preview-label]').textContent = sample.dataset.sampleLabel;
        samples.forEach((button, i) => button.setAttribute('aria-pressed', String(i === selected)));
        say(sample.dataset.sampleLabel);
      };
      samples.forEach((button, index) => button.addEventListener('click', () => select(index)));
      viewer.querySelector('[data-preview-prev]')?.addEventListener('click', () => select(selected - 1));
      viewer.querySelector('[data-preview-next]')?.addEventListener('click', () => select(selected + 1));
      viewer.querySelectorAll('[data-expand]').forEach(button => button.addEventListener('click', () => {
        openPage(samples[selected].dataset.sampleZoomSrc || samples[selected].dataset.sampleSrc, samples[selected].dataset.sampleLabel);
      }));
      viewer.addEventListener('keydown', event => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        select(selected + (event.key === 'ArrowRight' ? 1 : -1));
      });
    }

    root.querySelectorAll('[data-demo-download]').forEach(button => button.addEventListener('click', () => {
      document.querySelector('.demo-message').textContent = 'Design preview only. In the live store, your download starts here after payment confirmation.';
    }));
    initEditionPicker(root);
  }

  window.addEventListener('popstate', event => {
    if (!event.state?.concertScoresEdition || !document.querySelector('[data-edition-picker]')) return;
    replaceEditionPage(location.href, { pushHistory: false }).catch(() => location.reload());
  });

  initDynamicContent(document);
})();
